import _ from 'lodash';
import { OPERATORS, SOURCE_OPERATORS, SOURCE_TYPES, operatorsFor, isEligibleSource } from '../config/rules';

// Rule schema (stored in formBuilder.rules[]):
//   {
//     id: string,
//     when: 'all' | 'any',
//     conditions: [{ field: <source key>, op: <operator>, value?: <string> }],
//     actions:    [{ type: 'show' | 'hide', target: <target key> }]
//   }
//
// Operators mirror JotForm's Show/Hide conditions:
//   equal / not_equal              — value === / !== expected (choice & text)
//   contains / not_contains        — substring (case-insensitive)
//   starts_with / not_starts_with  — prefix (case-insensitive)
//   ends_with / not_ends_with      — suffix (case-insensitive)
//   less_than / greater_than       — numeric comparison
//   empty / not_empty              — presence
//   before / after                 — date comparison
//   equal_date / not_equal_date    — same calendar date
//   equal_day / not_equal_day      — same day-of-month
//
// Visibility contract:
//   - A field is visible by default unless `isHidden` is true on the field.
//   - A matched rule with action {type:'show', target} forces the field visible.
//   - A matched rule with action {type:'hide', target} forces the field hidden.
//   - Actions are applied top-to-bottom (rules in order, actions within a rule in
//     order); when several matched actions target the same field, the LAST one wins.
//   - If a field is hidden after evaluation, its value MUST be cleared by the
//     caller and omitted from the submission payload.

function isEmpty(value) {
    if (value === null || value === undefined || value === '') return true;
    // A single checkbox submits boolean false when unchecked — treat it as empty so
    // "is filled" (not_empty) means checked and "is empty" means unchecked (HF-1222).
    if (value === false) return true;
    if (_.isArray(value)) return value.length === 0;
    return false;
}

function asArray(value) {
    if (value === null || value === undefined) return [];
    return _.isArray(value) ? value : [value];
}

// Normalize a value to a lowercased string for case-insensitive text comparison.
function str(value) {
    return String(value ?? '').toLowerCase();
}

// Parse a value to a finite number, or null if not numeric.
function num(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

// Parse a date value to a timestamp (ms), or null. Accepts Date-parseable strings.
function dateTs(value) {
    if (value === null || value === undefined || value === '') return null;
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
}

// Extract a calendar day {y, m, d} from a date value WITHOUT timezone interpretation.
// A leading ISO date (YYYY-MM-DD...) is read literally from the string, so the same
// value yields the same day in JS and PHP regardless of server/browser timezone (a
// bare-datetime like "2020-06-16 00:00:01" would otherwise shift across midnight when
// parsed as local time). Falls back to UTC parsing for other formats.
function calendarParts(value) {
    if (value === null || value === undefined || value === '') return null;
    const s = String(value);
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        return { y: +iso[1], m: +iso[2], d: +iso[3] };
    }
    const t = dateTs(value);
    if (t === null) return null;
    const dt = new Date(t);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

// Day-of-month for a date value, or null.
function dayOfMonth(value) {
    const p = calendarParts(value);
    return p ? p.d : null;
}

// Normalize a date value to a comparable calendar day as an int YYYYMMDD,
// e.g. "2000-01-01" -> 20000101. Strips time-of-day and timezone so client and
// server (PHP) compare the same calendar day.
function dateDay(value) {
    const p = calendarParts(value);
    return p ? p.y * 10000 + p.m * 100 + p.d : null;
}

// Whether `raw` (scalar or array) equals `expected`. For arrays (multi-select),
// matches when the selected set contains expected (mirrors JotForm choice equal).
function valueEquals(raw, expected) {
    if (str(expected) === 'false' && isEmpty(raw)) {
        return true;
    }

    if (_.isArray(raw)) {
        if (raw.length === 1) {
            return str(raw[0]) === str(expected);
        }

        return asArray(raw).map(str).includes(str(expected));
    }

    return str(raw) === str(expected);
}

export function evaluateCondition(condition, formData) {
    const raw = formData[condition.field];
    const expected = condition.value;

    switch (condition.op) {
        case 'equal':
            return valueEquals(raw, expected);
        case 'not_equal':
            return !valueEquals(raw, expected);

        case 'contains':
            return str(raw).includes(str(expected));
        case 'not_contains':
            return !str(raw).includes(str(expected));
        case 'starts_with':
            return str(raw).startsWith(str(expected));
        case 'not_starts_with':
            return !str(raw).startsWith(str(expected));
        case 'ends_with':
            return str(raw).endsWith(str(expected));
        case 'not_ends_with':
            return !str(raw).endsWith(str(expected));

        case 'less_than': {
            const a = num(raw); const b = num(expected);
            return a !== null && b !== null && a < b;
        }
        case 'greater_than': {
            const a = num(raw); const b = num(expected);
            return a !== null && b !== null && a > b;
        }

        case 'empty':
            return isEmpty(raw);
        case 'not_empty':
            return !isEmpty(raw);

        case 'before': {
            const a = dateDay(raw); const b = dateDay(expected);
            return a !== null && b !== null && a < b;
        }
        case 'after': {
            const a = dateDay(raw); const b = dateDay(expected);
            return a !== null && b !== null && a > b;
        }
        case 'equal_date': {
            const a = dateDay(raw); const b = dateDay(expected);
            return a !== null && b !== null && a === b;
        }
        case 'not_equal_date': {
            const a = dateDay(raw); const b = dateDay(expected);
            return a !== null && b !== null && a !== b;
        }
        case 'equal_day': {
            const a = dayOfMonth(raw); const b = dayOfMonth(expected);
            return a !== null && b !== null && a === b;
        }
        case 'not_equal_day': {
            const a = dayOfMonth(raw); const b = dayOfMonth(expected);
            return a !== null && b !== null && a !== b;
        }

        default:
            return false;
    }
}

export function evaluateRule(rule, formData) {
    const conditions = rule.conditions || [];
    if (conditions.length === 0) return false;

    const when = rule.when === 'any' ? 'any' : 'all';
    const results = conditions.map(c => evaluateCondition(c, formData));

    return when === 'all' ? results.every(Boolean) : results.some(Boolean);
}

// Returns Map<fieldKey, boolean> — true means visible.
//
// Iterates to a fixpoint: a field hidden by one rule must not act as a source for
// another rule, so we clear hidden fields' values and re-evaluate until the hidden
// set stops changing. Matches the PHP RuleEvaluator and JotForm semantics (a hidden
// field "doesn't exist").
export function computeVisibility(allFields, rules, formData) {
    let visibility = new Map();
    const setDefaults = (map) => allFields.forEach(f => map.set(f.key, !f.isHidden));
    setDefaults(visibility);

    const maxPasses = (rules || []).length + 1;
    let prevHidden = null;

    for (let pass = 0; pass < maxPasses; pass++) {
        // Effective data for this pass: blank out anything currently hidden.
        const data = Object.assign({}, formData);
        visibility.forEach((visible, key) => {
            if (visible === false) delete data[key];
        });

        const passVis = new Map();
        setDefaults(passVis);

        // Apply matched actions top-to-bottom: rules in order, actions within a rule in
        // order. Each action overwrites the target's visibility, so the LAST matched
        // action targeting a field wins (matches the order shown in the builder).
        (rules || []).forEach(rule => {
            if (!evaluateRule(rule, data)) return;
            (rule.actions || []).forEach(action => {
                if (!action.target) {
                    return;
                }

                if (action.type === 'show') {
                    passVis.set(action.target, true);
                } else if (action.type === 'hide') {
                    passVis.set(action.target, false);
                }
            });
        });

        visibility = passVis;

        const hidden = [];
        visibility.forEach((v, k) => { if (v === false) hidden.push(k); });
        hidden.sort();
        const hiddenStr = hidden.join(' ');
        if (prevHidden !== null && hiddenStr === prevHidden) break; // fixpoint reached
        prevHidden = hiddenStr;
    }

    return visibility;
}

// Keys of fields that must be treated as required for THIS submission because a Show
// rule revealed them. A field hidden by default (`isHidden`) and marked `isRequired`
// is NOT required while hidden — only when conditional logic makes it visible. Keys
// are lowercased to match validation rules (which lowercase field keys). Mirrors the
// PHP RuleEvaluator::conditionalRequiredKeys.
export function conditionalRequiredKeys(allFields, visibility) {
    const keys = [];
    (allFields || []).forEach(field => {
        const key = field.key;
        if (key === null || key === undefined || key === '') return;
        if (!field.isHidden || !field.isRequired) return;
        const visible = visibility instanceof Map ? visibility.get(key) : visibility[key];
        if (visible === true) keys.push(String(key).toLowerCase());
    });
    return Array.from(new Set(keys));
}

// Convenience: flatten formBuilder.items[step].fields[] into a single array.
export function flattenFields(formBuilderItems) {
    const out = [];
    (formBuilderItems || []).forEach(step => {
        (step.fields || []).forEach(f => out.push(f));
    });
    return out;
}

export default {
    evaluateCondition,
    evaluateRule,
    computeVisibility,
    conditionalRequiredKeys,
    flattenFields,
    operatorsFor,
    isEligibleSource,
    OPERATORS,
    SOURCE_OPERATORS,
    SOURCE_TYPES,
};
